import { useCallback, useRef } from "react";

import { useDrag, useDrop } from "react-dnd";

import type { Identifier, XYCoord } from "dnd-core";

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export function useDraggable<T>(
  type: string,
  index: number,
  item: T,
  move: (dragIndex: number, hoverIndex: number) => void,
) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    unknown,
    { handlerId: Identifier | null }
  >({
    accept: type,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collect(monitor: any) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(i: DragItem, monitor: { getClientOffset: () => unknown }) {
      if (!nodeRef.current) {
        return;
      }
      const dragIndex = i.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = nodeRef.current.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      move(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // eslint-disable-next-line no-param-reassign
      i.index = hoverIndex;
    },
  });

  const [{ opacity }, drag] = useDrag(
    () => ({
      type,
      item: { item, index },
      collect: (monitor: { isDragging: () => boolean }) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
      }),
    }),
    [],
  );

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node;
      drag(drop(node));
    },
    [drag, drop],
  );

  return { handlerId, opacity, ref };
}
